import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCheck,
  ChevronDown,
  Filter,
  Info,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react'
import { toast } from 'sonner'

import type { CommonPagination } from '@/types/common'
import type {
  AdminNotification,
  AdminNotificationType,
  AdminNotificationsQuery,
} from '@/types/notification'
import {
  AdminNotificationSeverity,
  NOTIFICATION_SEVERITY_LABELS,
  NOTIFICATION_TYPE_LABELS,
  READ_STATUS_OPTIONS,
  TIME_RANGE_OPTIONS,
} from '@/types/notification'
import TitleHeader from '@/components/TitleHeader'
import { Card, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { NotificationsService } from '@/services/notifications'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/notifications/')({
  component: NotificationsManagement,
})

function getDateRange(range: string): { dateFrom?: string; dateTo?: string } {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  switch (range) {
    case 'today':
      return { dateFrom: today.toISOString() }
    case '7days': {
      const days7 = new Date(today)
      days7.setDate(days7.getDate() - 7)
      return { dateFrom: days7.toISOString() }
    }
    case '30days': {
      const days30 = new Date(today)
      days30.setDate(days30.getDate() - 30)
      return { dateFrom: days30.toISOString() }
    }
    default:
      return {}
  }
}

function getSeverityIcon(severity: AdminNotificationSeverity) {
  switch (severity) {
    case AdminNotificationSeverity.CRITICAL:
      return <AlertCircle className="w-5 h-5 text-red-500" />
    case AdminNotificationSeverity.WARNING:
      return <AlertTriangle className="w-5 h-5 text-yellow-500" />
    case AdminNotificationSeverity.INFO:
    default:
      return <Info className="w-5 h-5 text-blue-500" />
  }
}

function getSeverityBadgeVariant(
  severity: AdminNotificationSeverity,
): 'destructive' | 'secondary' | 'default' {
  switch (severity) {
    case AdminNotificationSeverity.CRITICAL:
      return 'destructive'
    case AdminNotificationSeverity.WARNING:
      return 'secondary'
    case AdminNotificationSeverity.INFO:
    default:
      return 'default'
  }
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Vừa xong'
  if (diffMins < 60) return `${diffMins} phút trước`
  if (diffHours < 24) return `${diffHours} giờ trước`
  if (diffDays < 7) return `${diffDays} ngày trước`
  return date.toLocaleDateString('vi-VN')
}

function NotificationsManagement() {
  const [query, setQuery] = useState<AdminNotificationsQuery>({
    take: 10,
    page: 1,
    sorts: ['createdAt.DESC'],
  })
  const [filters, setFilters] = useState({
    type: 'all',
    severity: 'all',
    isRead: 'all',
    timeRange: 'all',
  })
  const [dataNotifications, setDataNotifications] = useState<
    CommonPagination<AdminNotification>
  >({
    items: [],
    meta: {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
    },
  })
  const [unreadCount, setUnreadCount] = useState(0)

  const mutationGetList = useMutation({
    mutationFn: (params: AdminNotificationsQuery) => {
      return NotificationsService.getList(params)
    },
    onSuccess: (response) => {
      setDataNotifications(response.data)
    },
  })

  const mutationGetUnreadCount = useMutation({
    mutationFn: () => NotificationsService.getUnreadCount(),
    onSuccess: (response) => {
      setUnreadCount(response.data.count)
    },
  })

  const mutationMarkAsRead = useMutation({
    mutationFn: (id: string) => NotificationsService.markAsRead(id),
    onSuccess: () => {
      fetchData()
      mutationGetUnreadCount.mutate()
    },
  })

  const mutationMarkAllAsRead = useMutation({
    mutationFn: () => NotificationsService.markAllAsRead(),
    onSuccess: (response) => {
      toast.success(`Đã đánh dấu ${response.data.affected} thông báo đã đọc`)
      fetchData()
      mutationGetUnreadCount.mutate()
    },
  })

  const fetchData = (customQuery?: AdminNotificationsQuery) => {
    mutationGetList.mutate(customQuery || query)
  }

  useEffect(() => {
    fetchData()
    mutationGetUnreadCount.mutate()
  }, [])

  const handleSearch = () => {
    const newQuery: AdminNotificationsQuery = {
      ...query,
      page: 1,
      type:
        filters.type !== 'all'
          ? (filters.type as AdminNotificationType)
          : undefined,
      severity:
        filters.severity !== 'all'
          ? (filters.severity as AdminNotificationSeverity)
          : undefined,
      isRead: filters.isRead === 'all' ? undefined : filters.isRead === 'true',
      ...getDateRange(filters.timeRange),
    }
    setQuery(newQuery)
    fetchData(newQuery)
  }

  const handleClear = () => {
    setFilters({
      type: 'all',
      severity: 'all',
      isRead: 'all',
      timeRange: 'all',
    })
    const newQuery = { take: 10, page: 1, sorts: ['createdAt.DESC'] }
    setQuery(newQuery)
    fetchData(newQuery)
  }

  const handleRefresh = () => {
    fetchData()
    mutationGetUnreadCount.mutate()
  }

  const handleMarkAsRead = (id: string) => {
    mutationMarkAsRead.mutate(id)
  }

  const handleMarkAllAsRead = () => {
    mutationMarkAllAsRead.mutate()
  }

  const hasNextPage = dataNotifications.meta.hasNext
  const hasPrevPage = dataNotifications.meta.hasPrev

  const handlePrevPage = () => {
    if (hasPrevPage) {
      const newQuery = { ...query, page: (query.page ?? 1) - 1 }
      setQuery(newQuery)
      fetchData(newQuery)
    }
  }

  const handleNextPage = () => {
    if (hasNextPage) {
      const newQuery = { ...query, page: (query.page ?? 1) + 1 }
      setQuery(newQuery)
      fetchData(newQuery)
    }
  }

  const isLoading = mutationGetList.isPending

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70">
          <Loader2 className="animate-spin w-12 h-12 text-primary" />
        </div>
      )}

      <TitleHeader
        title="Thông báo"
        subtitle="Theo dõi các sự kiện quan trọng của hệ thống"
        action={
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-sm px-3 py-1">
                {unreadCount} chưa đọc
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw
                className={cn('w-4 h-4 mr-1', isLoading && 'animate-spin')}
              />
              Làm mới
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0 || mutationMarkAllAsRead.isPending}
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Đánh dấu tất cả đã đọc
            </Button>
          </div>
        }
      />

      <details
        open
        className="mt-4 rounded-lg border bg-blue-50/50 border-blue-200 group"
      >
        <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-blue-700 select-none flex items-center gap-2">
          <Info className="w-4 h-4" />
          Các sự kiện sẽ tạo thông báo
          <ChevronDown className="w-4 h-4 ml-auto transition-transform group-open:rotate-180" />
        </summary>
        <div className="px-4 pb-3 grid grid-cols-2 gap-1 text-sm text-gray-600">
          <span>🛒 Đơn hàng mới được tạo</span>
          <span>✅ Đơn hàng thanh toán thành công</span>
          <span>❌ Đơn hàng thất bại</span>
          <span>💰 Nhận giao dịch ngân hàng mới</span>
          <span>💳 Nạp tiền lớn vào ví (&ge; 1,000,000₫)</span>
          <span>👤 Người dùng mới đăng ký</span>
          <span>⚠️ Lỗi kết nối API sản phẩm</span>
          <span>🎁 Khuyến mãi sắp hết hạn (&le; 3 ngày)</span>
          <span>📊 Quota khuyến mãi còn ít (&le; 5)</span>
        </div>
      </details>

      <Card className="mt-6 shadow-none">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Danh sách thông báo
          </CardTitle>
        </CardHeader>

        <Separator />

        <div className="p-6 space-y-6">
          {/* Filters */}
          <div className="bg-white rounded-lg space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <Select
                value={filters.isRead}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, isRead: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Trạng thái đọc" />
                </SelectTrigger>
                <SelectContent>
                  {READ_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.type}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    type: value === 'all' ? '' : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Loại thông báo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả loại</SelectItem>
                  {Object.entries(NOTIFICATION_TYPE_LABELS).map(
                    ([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>

              <Select
                value={filters.severity}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    severity: value === 'all' ? '' : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Mức độ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả mức độ</SelectItem>
                  {Object.entries(NOTIFICATION_SEVERITY_LABELS).map(
                    ([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>

              <Select
                value={filters.timeRange}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    timeRange: value === 'all' ? '' : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Thời gian" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_RANGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Button variant="outline" className="mr-3" onClick={handleSearch}>
                <Search className="w-4 h-4 mr-1" />
                Tìm kiếm
              </Button>
              <Button variant="outline" className="mr-3" onClick={handleClear}>
                <Filter className="w-4 h-4 mr-1" />
                Xoá bộ lọc
              </Button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="space-y-3">
            {dataNotifications.items.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Không có thông báo nào</p>
              </div>
            ) : (
              dataNotifications.items.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'flex items-start gap-4 p-4 rounded-lg border transition-colors cursor-pointer hover:bg-gray-50',
                    !notification.isRead && 'bg-blue-50/50 border-blue-200',
                  )}
                  onClick={() => {
                    if (!notification.isRead) {
                      handleMarkAsRead(notification.id)
                    }
                  }}
                >
                  <div className="flex-shrink-0 mt-1">
                    {getSeverityIcon(notification.severity)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">
                        {notification.title}
                      </span>
                      <Badge
                        variant={getSeverityBadgeVariant(notification.severity)}
                        className="text-xs"
                      >
                        {NOTIFICATION_SEVERITY_LABELS[notification.severity]}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {NOTIFICATION_TYPE_LABELS[notification.type]}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>{formatTimeAgo(notification.createdAt)}</span>
                      {notification.isRead && (
                        <span className="flex items-center gap-1">
                          <CheckCheck className="w-3 h-3" />
                          Đã đọc
                        </span>
                      )}
                    </div>
                  </div>
                  {!notification.isRead && (
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <Separator />

        <CardFooter className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Hiển thị {dataNotifications.items.length} /{' '}
            {dataNotifications.meta.total} thông báo
          </div>
          <div>
            <Pagination>
              <PaginationContent>
                <PaginationItem onClick={handlePrevPage}>
                  <PaginationPrevious />
                </PaginationItem>
                <PaginationItem onClick={handleNextPage}>
                  <PaginationNext />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}
